import React, {useEffect, useState} from "react";
import NarrowDrawer from "../../../../components/general/NarrowDrawer.tsx";
import {User} from "../../../../api/accessControl/User.ts";
import {BackendApi} from "../../../../api/api.ts";
import LoadingFrame from "../../../../components/LoadingFrame.tsx";
import {PrimaryButton} from "../../../../components/general/PrimaryButton.tsx";
import {ComboboxList} from "../../../../components/general/ComboboxList.tsx";
import Dropdown from "../../../../components/general/Dropdown.tsx";
import {BuildingOfficeIcon, XMarkIcon} from "@heroicons/react/24/outline";

interface BusinessOption {
    id: string;
    name: string;
    displayName: string;
}

interface UserDetailsDrawerProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    userId: string | null;
    currentUser: User;
}

export default function UserDetailsDrawer({open, setOpen, userId, currentUser}: UserDetailsDrawerProps) {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [userDetails, setUserDetails] = useState<any>(null);
    const [accessList, setAccessList] = useState<any>([]);

    const [allBusinesses, setAllBusinesses] = useState<BusinessOption[]>([]);
    const [selectedBusiness, setSelectedBusiness] = useState<BusinessOption | null>(null);
    const [selectedRole, setSelectedRole] = useState<string>('entrepreneur');
    const [isGranting, setIsGranting] = useState<boolean>(false);

    const isViewingSelf = userId === currentUser.id;
    const availableRoles = isViewingSelf
        ? [{value: 'entrepreneur', label: 'Entrepreneur'}, {value: 'partner', label: 'Partner'}, {
            value: 'admin',
            label: 'Admin'
        }]
        : [{value: 'entrepreneur', label: 'Entrepreneur'}, {value: 'partner', label: 'Partner'}];

    async function loadBusinesses() {
        const api = new BackendApi(currentUser.getToken());
        try {
            const json = await api.Admin__businesses({});
            const businesses = (Array.isArray(json) ? json : []).map((b: any) => ({
                id: b.id,
                name: b.name,
                displayName: b.name,
            }));
            setAllBusinesses(businesses);
        } catch (e) {
            console.error('Error loading businesses:', e);
        }
    }

    async function loadData() {
        if (!userId || !open) return;
        setIsLoading(true);
        const api = new BackendApi(currentUser.getToken());
        try {
            const json = await api.Admin__getUserDetails({user_id: userId});
            if (json && json.data && json.data.user) {
                setUserDetails(json.data.user);
                setAccessList(json.data.accesses || []);
            }
        } catch (e) {
            console.error(e);
            alert("Error loading user details: " + e);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (open && userId) {
            loadData();
            loadBusinesses();
        } else {
            setUserDetails(null);
            setAccessList([]);
            setSelectedBusiness(null);
        }
    }, [open, userId]);

    async function handleGrantAccess() {
        if (!userId || !selectedBusiness) return;
        setIsGranting(true);
        const api = new BackendApi(currentUser.getToken());
        try {
            await api.Admin__grantBusinessAccess({
                user_id: userId,
                business_id: selectedBusiness.id,
                role: selectedRole,
            });
            setSelectedBusiness(null);
            setSelectedRole('entrepreneur');
            await loadData();
        } catch (e) {
            console.error(e);
            alert("Error granting access: " + e);
        } finally {
            setIsGranting(false);
        }
    }

    async function handleRevokeAccess(businessId: string, businessName: string) {
        if (!confirm(`Are you sure you want to revoke access to "${businessName}"?`)) return;
        const api = new BackendApi(currentUser.getToken());
        try {
            await api.Admin__revokeBusinessAccess({
                user_id: userId,
                business_id: businessId
            });
            await loadData();
        } catch (e) {
            console.error(e);
            alert("Error revoking access: " + e);
        }
    }

    return (
        <NarrowDrawer title="User Details" open={open} updateOpenState={setOpen}>
            <LoadingFrame isLoading={isLoading}>
                {userDetails && (
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-lg font-medium leading-6 text-gray-900">Personal Information</h3>
                            <dl className="mt-2 divide-y divide-gray-200 border-t border-b border-gray-200">
                                <div className="py-3 flex justify-between text-sm font-medium">
                                    <dt className="text-gray-500">ID</dt>
                                    <dd className="text-gray-900 font-mono text-xs">{userDetails.id}</dd>
                                </div>
                                <div className="py-3 flex justify-between text-sm font-medium">
                                    <dt className="text-gray-500">Full name</dt>
                                    <dd className="text-gray-900">{userDetails.first_name} {userDetails.last_name}</dd>
                                </div>
                                <div className="py-3 flex justify-between text-sm font-medium">
                                    <dt className="text-gray-500">Email</dt>
                                    <dd className="text-gray-900">{userDetails.email}</dd>
                                </div>
                            </dl>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-3">Business Access</h3>
                            {accessList.length > 0 ? (
                                <div className="space-y-2">
                                    {accessList.map((access: any, idx: number) => (
                                        <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400 shrink-0"/>
                                                    <span
                                                        className="font-medium text-sm text-gray-900">{access.business_name}</span>
                                                </div>
                                                <button
                                                    onClick={() => handleRevokeAccess(access.business_id, access.business_name)}
                                                    className="rounded-md p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
                                                >
                                                    <XMarkIcon className="h-4 w-4"/>
                                                </button>
                                            </div>
                                            <div className="mt-1.5 ml-7 flex items-center gap-2">
                                                <span
                                                    className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                                                    {access.role_name}
                                                </span>
                                                <span
                                                    className="text-gray-400 font-mono text-[10px]">{access.business_id}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="mt-2 text-sm text-gray-500">No business access found.</p>
                            )}
                        </div>

                        <div>
                            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-2">Grant New Access</h3>
                            <div className="flex items-end gap-2">
                                <div className="flex-1">
                                    <ComboboxList<BusinessOption>
                                        options={allBusinesses}
                                        selected={selectedBusiness}
                                        onSelect={(option) => setSelectedBusiness(option)}
                                        emptySelectionMessage="Search for a business..."
                                        internalMapper={(b) => ({...b, displayName: b.name})}
                                        showClearButton={true}
                                        anchor={{to: "bottom start", gap: 4}}
                                        menuWidth="w-[var(--input-width)]"
                                    />
                                </div>
                                <div className="shrink-0 w-40">
                                    <Dropdown
                                        selected={{
                                            value: selectedRole,
                                            label: availableRoles.find(r => r.value === selectedRole)?.label
                                        }}
                                        options={availableRoles.map(r => ({value: r.value, label: r.label}))}
                                        onUpdateFunc={(value) => setSelectedRole(value as string)}
                                    />
                                </div>
                                <PrimaryButton
                                    className="shrink-0"
                                    onClick={handleGrantAccess}
                                    disabled={!selectedBusiness || isGranting}
                                    title={isGranting ? "Granting..." : "Grant"}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </LoadingFrame>
        </NarrowDrawer>
    );
}
